import React from 'react';
import DonutChart from 'components/pure/charts/DonutChart';
import LoadingOverlay from 'components/pure/utils/LoadingOverlay';
import RepProfilePageActions from 'actions/RepProfilePageActions';
import RepProfileSectionHeader from './RepProfileSectionHeader';
import RepActivityItem from './RepActivityItem';
import DonutTooltipContent from './tooltips/DonutTooltipContent';
import RepProfileDonutLabel from './RepProfileDonutLabel';
import HelpText from './tooltips/HelpText';
import formatSecondsShort from 'utils/formatSecondsShort';
import round from 'utils/round';
import './RepProfileActivities.styl';
import {getMuiV4Theme} from 'utils/css/themeUtils';

const disclaimer =
  "Note that this includes only calls longer than the minimum duration configured for the rep's department; i.e. calls under this duration are not included in the total.";

const overAnHour = value => Math.round(value / 3600) > 0;

export default class RepProfileCallActivities extends React.PureComponent {
  render() {
    const {
      callActivity,
      callDonut,
      callTeamMetrics,
      rep,
      isCallActivityLoading,
      isCallDonutLoading,
      isCallTeamMetricsLoading,
      periodText,
      startDate,
      endDate,
      headerStartDate,
      headerEndDate,
      renderTeamAverage,
      users,
    } = this.props;
    const pieData =
      callDonut &&
      callDonut.map(stats => ({
        id: stats.userId,
        user: users.get(stats.userId),
        value: stats.score,
        selected: rep && rep.id === stats.userId,
      }));
    const pieTotal = pieData && pieData.reduce((memo, d) => memo + d.value, 0);
    const avgCallDurationOverAnHour =
      callTeamMetrics && overAnHour(callTeamMetrics.avgCallDurationSecondsMax);
    const avgCallDurationLabel = avgCallDurationOverAnHour ? 'Hours' : 'Minutes';
    const totalCallDurationOverAnHour =
      callTeamMetrics && overAnHour(callTeamMetrics.totalCallDurationSecondsMax);
    const totalCallDurationLabel = totalCallDurationOverAnHour ? 'Hours' : 'Minutes';

    const dataGetterForType = type => () =>
      RepProfilePageActions.getTrendData({
        userId: rep.id,
        type,
        startDate,
        endDate,
      });

    const theme = getMuiV4Theme();

    return callActivity || isCallDonutLoading || isCallActivityLoading ? (
      <section className="rep-profile-activities main-section">
        {callActivity && (
          <section className="stats">
            {(isCallActivityLoading || isCallTeamMetricsLoading) && <LoadingOverlay />}
            <RepProfileSectionHeader
              rep={rep}
              title={(rep ? `${rep.firstName}'s ` : '') + 'Call Activities Compared to Team'}
              startDate={headerStartDate}
              endDate={headerEndDate}
              location={this.props.location}
            />
            <div className="secondary-section-header">Activities</div>
            <div className="sub-section">
              <RepActivityItem
                startDate={startDate}
                endDate={endDate}
                rep={rep}
                repValue={callActivity.callsPlaced}
                teamAverage={renderTeamAverage && callTeamMetrics.callsPlacedAvg}
                max={
                  callTeamMetrics.callsPlacedMax == null
                    ? Number.MAX_VALUE
                    : callTeamMetrics.callsPlacedMax
                }
                title="Conversations"
                color={theme.palette.primary.main}
                fetchTrendData={dataGetterForType('conversations')}
                helpText={() => (
                  <HelpText
                    text={`How many calls the rep made or received during the ${periodText} period of the report.`}
                    disclaimer={disclaimer}
                  />
                )}
              />
              <RepActivityItem
                startDate={startDate}
                endDate={endDate}
                rep={rep}
                repValue={callActivity.totalCallDurationSeconds}
                teamAverage={renderTeamAverage && callTeamMetrics.totalCallDurationSecondsAvg}
                max={
                  callTeamMetrics.totalCallDurationSecondsMax == null
                    ? Number.MAX_VALUE
                    : callTeamMetrics.totalCallDurationSecondsMax
                }
                title={`${totalCallDurationLabel} Total Call Duration`}
                tooltipTitle={`Total Call Duration in ${totalCallDurationLabel}`}
                color={theme.palette.primary.main}
                fetchTrendData={dataGetterForType('totalduration')}
                formatValue={v => formatSecondsShort(v, totalCallDurationOverAnHour, false, false)}
                helpText={() => (
                  <HelpText
                    text={`The total duration of calls that the rep made or received during the ${periodText} period of the report.`}
                    disclaimer={disclaimer}
                  />
                )}
              />
              <RepActivityItem
                startDate={startDate}
                endDate={endDate}
                rep={rep}
                repValue={callActivity.avgCallDurationSeconds}
                teamAverage={renderTeamAverage && callTeamMetrics.avgCallDurationSecondsAvg}
                max={
                  callTeamMetrics.avgCallDurationSecondsMax == null
                    ? Number.MAX_VALUE
                    : callTeamMetrics.avgCallDurationSecondsMax
                }
                title={`${avgCallDurationLabel} Average Call Duration`}
                tooltipTitle={`Average Call Duration in ${avgCallDurationLabel}`}
                color={theme.palette.primary.main}
                fetchTrendData={dataGetterForType('avgduration')}
                formatValue={v => formatSecondsShort(v, avgCallDurationOverAnHour)}
                helpText={() => (
                  <HelpText
                    text={`The average duration of calls that the rep made or received during the ${periodText} period of the report.`}
                    disclaimer={disclaimer}
                  />
                )}
              />
              <RepActivityItem
                startDate={startDate}
                endDate={endDate}
                rep={rep}
                repValue={callActivity.talkListenRatio}
                teamAverage={renderTeamAverage && callTeamMetrics.talkListenRatioAvg}
                max={
                  callTeamMetrics.talkListenRatioMax == null
                    ? Number.MAX_VALUE
                    : callTeamMetrics.talkListenRatioMax
                }
                formatValue={v => round(v * 100) + '%'}
                title="Talk / Listen Ratio"
                color={theme.palette.primary.main}
                fetchTrendData={dataGetterForType('talklisten')}
                showMax={false}
                helpText={() => (
                  <HelpText
                    text={`The percentage of time the rep listened to the other party during calls they made or received in the ${periodText} period of the report. For example, 100% would be entirely listening, and 0% would be entirely talking.`}
                    disclaimer={disclaimer}
                  />
                )}
              />
              <RepActivityItem
                startDate={startDate}
                endDate={endDate}
                rep={rep}
                repValue={callActivity.avgInterchangesPerMinute}
                teamAverage={renderTeamAverage && callTeamMetrics.avgInterchangesPerMinuteAvg}
                max={
                  callTeamMetrics.avgInterchangesPerMinuteMax == null
                    ? Number.MAX_VALUE
                    : callTeamMetrics.avgInterchangesPerMinuteMax
                }
                title="Interchanges Per Minute"
                color={theme.palette.primary.main}
                fetchTrendData={dataGetterForType('interchanges')}
                helpText={() => (
                  <HelpText
                    text={`The average number of interchanges per minute of conversation across calls made or received by the rep during the ${periodText} period of the report. An interchange is defined as one complete back and forth exchange between rep and prospect.`}
                    disclaimer={disclaimer}
                  />
                )}
              />
            </div>
          </section>
        )}
        {pieData && (
          <DonutChart
            data={pieData}
            height={850}
            width={700}
            padAngle={0.015}
            label={percentage => (
              <RepProfileDonutLabel
                rep={rep}
                percentage={percentage}
                labelText="team calling activity"
                helpText={() => (
                  <HelpText
                    text={`The pie chart shows the call activity of reps in this team for the ${periodText} period of the report, ordered by ranking. The rep being reported on is highlighted. The call activity score is a weighted combination of the activity metrics to the left.`}
                    disclaimer={disclaimer}
                  />
                )}
              />
            )}
            color={theme.palette.primary.main}
            tooltipContent={data => (
              <DonutTooltipContent
                user={data.user}
                label="team calling activity"
                total={pieTotal}
                value={data.value}
                onClick={() => this.props.onSelectRep(data.userId)}
              />
            )}
          />
        )}
      </section>
    ) : null;
  }
}
